import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Loader from "../../common/spinner";
import ConfirmModal from "../../common/ConfirmModal";
import classes from "./checkout-form.module.scss";

const VOCartPreview = (props) => {
  const inr = new Intl.NumberFormat("en-IN", { style: "decimal" });
  const [payNowLoading, setPayNowLoading] = useState(false);
  const [paymentWarning, setPaymentWarning] = useState(false);
  // The item is kept in state after the modal closes so its name stays
  // rendered through the close animation. Visibility is driven by
  // `confirmOpen` alone.
  const [itemToRemove, setItemToRemove] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const lastCartRef = React.useRef(props.cart);
  const lastSummeryRef = React.useRef(null);
  const validAppliedPaymentMethod =
    props.VOData &&
    props.VOData.order &&
    props.VOData.order.applied_payment_method &&
    props.VOData.options &&
    props.VOData.options.payment_methods &&
    Object.values(props.VOData.options.payment_methods).some(
      (method) =>
        method.id ===
        props.VOData.order.applied_payment_method.virtual_order_payment_mode,
    )
      ? props.VOData.order.applied_payment_method
      : null;

  // An applied gift card only counts once its OTP transfer is verified. While it
  // is pending, block order placement so we never submit an unlocked gift card.
  const appliedGiftCard =
    props.VOData && props.VOData.order && props.VOData.order.applied_gift_card;
  const giftCardPending = !!(
    appliedGiftCard &&
    appliedGiftCard.virtual_order_redeem_status !== "transfer_verified"
  );

  if (props.cart && props.cart.length > 0) {
    lastCartRef.current = props.cart;
  }
  if (props.VOData && props.VOData.order && props.VOData.order.summery) {
    lastSummeryRef.current = props.VOData.order.summery;
  }

  useEffect(
    function() {
      if (!props.syncing) setPayNowLoading(false);
    },
    [props.syncing],
  );

  useEffect(
    function() {
      if (validAppliedPaymentMethod) {
        setPaymentWarning(false);
      }
    },
    [validAppliedPaymentMethod],
  );

  const requestRemove = (item) => {
    setItemToRemove(item);
    setConfirmOpen(true);
  };

  const confirmRemove = () => {
    setConfirmOpen(false);
    if (!itemToRemove) return;
    props.removeFromCart(itemToRemove.cart_product_data.product_id, {
      id: itemToRemove.cart_product_data.id,
    });
  };

  const img_fallback = (event) => {
    event.target.parentNode.childNodes.forEach(function(node) {
      if (node.nodeName == "IMG") node.src = window.no_image;
    });
  };

  const loadingPreview = () => {
    return (
      <div className={classes.orderSummary}>
        <div className={classes.skeletonTitle} />
        <div className={classes.orderItems}>
          <div className={classes.orderItem}>
            <div
              className={classes.skeleton}
              style={{ width: 72, height: 72, flexShrink: 0 }}
            />
            <div style={{ flex: 1 }}>
              <div
                className={classes.skeleton}
                style={{ height: 14, width: "80%", marginBottom: 8 }}
              />
              <div
                className={classes.skeleton}
                style={{ height: 12, width: "50%", marginBottom: 12 }}
              />
              <div
                className={classes.skeleton}
                style={{ height: 14, width: "60%" }}
              />
            </div>
          </div>
        </div>
        <hr className={classes.orderDivider} />
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div
            className={classes.skeleton}
            style={{ height: 14, width: "70%" }}
          />
          <div
            className={classes.skeleton}
            style={{ height: 14, width: "60%" }}
          />
          <div
            className={classes.skeleton}
            style={{ height: 14, width: "50%" }}
          />
          <div
            className={classes.skeleton}
            style={{ height: 14, width: "65%" }}
          />
        </div>
        <div
          className={classes.skeleton}
          style={{ height: 48, width: "100%", marginTop: 24 }}
        />
      </div>
    );
  };

  const cartPreview = () => {
    var cart =
      props.cart && props.cart.length > 0 ? props.cart : lastCartRef.current;
    if (!cart || cart.length === 0) return loadingPreview();
    const items = cart.map((item, index) => (
      <div className={classes.orderItem} key={index}>
        <img
          className={classes.orderItemImg}
          src={`${
            (item.cart_product_image || "").split(",")[0]
          }?resize=98,98&compress=50`}
          onError={img_fallback}
          alt={item.cart_product_name}
        />
        <div className={classes.orderItemInfo}>
          <div className={classes.orderItemHeader}>
            <p className={classes.orderItemName}>{item.cart_product_name}</p>
            <button
              className={classes.orderItemRemove}
              onClick={() => requestRemove(item)}
            >
              &times;
            </button>
          </div>
          <p className={classes.orderItemSku}>{item.cart_product_sku}</p>
          <div className={classes.orderItemPricing}>
            <p className={classes.orderItemQty}>
              {item.cart_product_quantity} X {props.symbol}
              {inr.format(item.cart_product_unit_price)}
            </p>
            <p className={classes.orderItemTotal}>
              {props.symbol}{" "}
              {inr.format(
                item.cart_product_quantity * item.cart_product_unit_price,
              )}
            </p>
          </div>
        </div>
      </div>
    ));

    var summery =
      props.VOData && props.VOData.order && props.VOData.order.summery
        ? props.VOData.order.summery
        : lastSummeryRef.current;
    if (!summery) return loadingPreview();

    return (
      <>
      <div className={classes.orderSummary}>
        <div className={classes.orderTitleRow}>
          <h3 className={classes.orderTitle}>Order Summary</h3>
          <Link
            to={`${process.env.PUBLIC_URL}/cart`}
            className={classes.editCartLink}
          >
            Edit Cart
          </Link>
        </div>

        <div
          className={`${classes.orderItems} ${
            cart.length > 3 ? classes.orderItemsScrollable : ""
          }`}
        >
          {items}
        </div>

        <hr className={classes.orderDivider} />

        <div className={classes.orderPricing}>
          <div className={classes.orderPricingRow}>
            <p className={classes.orderPricingLabel}>Subtotal</p>
            <p className={classes.orderPricingValue}>
              {props.symbol} {inr.format(summery.subtotal)}
            </p>
          </div>
          <div className={classes.orderPricingRow}>
            <p className={classes.orderPricingLabel}>Coupon Code Discount</p>
            <p className={classes.orderPricingValue}>
              {props.symbol}
              {inr.format(summery.discounts)}
            </p>
          </div>
          <div className={classes.orderPricingRow}>
            <p className={classes.orderPricingLabel}>Redeemed Amount</p>
            <p className={classes.orderPricingValue}>
              {props.symbol}
              {inr.format(summery.redeems)}
            </p>
          </div>
          <div className={classes.orderPricingRow}>
            <p className={classes.orderPricingLabel}>Service Charges</p>
            <p className={classes.orderPricingValue}>
              {summery.charges > 0
                ? `${props.symbol}${inr.format(summery.charges)}`
                : "Free"}
            </p>
          </div>
        </div>

        <hr className={classes.orderTotalDivider} />

        <div className={classes.orderTotalRow}>
          <p className={classes.orderTotalLabel}>Total</p>
          <p className={classes.orderTotalValue}>
            {props.symbol} {inr.format(summery.payable_amount)}
          </p>
        </div>

        <div className={classes.orderPayBar}>
          {paymentWarning && (
            <p className={classes.paymentWarning}>
              Please select a payment method to continue.
            </p>
          )}
          {giftCardPending && (
            <p className={classes.paymentWarning}>
              Please verify your gift card OTP to place the order.
            </p>
          )}
          {payNowLoading ? (
            <button className={classes.orderPayBtn} disabled>
              <Loader loaderColor="#fff" />
            </button>
          ) : (
            <button
              className={classes.orderPayBtn}
              disabled={props.syncing || giftCardPending}
              onClick={function() {
                var result = props.updateVO();
                if (result === "address_error") {
                  setPaymentWarning(false);
                } else if (result === "payment_error") {
                  setPaymentWarning(true);
                } else {
                  setPaymentWarning(false);
                  setPayNowLoading(true);
                }
              }}
            >
              {props.VOData &&
              validAppliedPaymentMethod &&
              (validAppliedPaymentMethod.virtual_order_payment_mode ===
                "payment_on_delivery" ||
                validAppliedPaymentMethod.virtual_order_payment_mode ===
                  "gift_card_full")
                ? "Place Order"
                : "Pay Now"}
            </button>
          )}
        </div>
      </div>
      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={confirmRemove}
        title="Remove item?"
        message={
          itemToRemove
            ? `Are you sure you want to remove "${itemToRemove.cart_product_name}" from your cart?`
            : ""
        }
        confirmText="Remove"
        cancelText="Cancel"
      />
      </>
    );
  };

  if (props.syncing && (!props.VOData || !props.VOData.order))
    return loadingPreview();
  else return cartPreview();
};

export default VOCartPreview;
